import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  Paperclip,
  UserPlus,
  X,
} from 'react-feather';
import Avatar from './Avatar';
import EmailHtmlBody from './EmailHtmlBody';
import { esCandidatoCc, tamanoLegible } from '../../features/tickets';

// Solo permitimos esquemas seguros en el href del adjunto (la ruta viene del
// backend/Drive, pero blindamos el render contra javascript:/data: por si acaso).
const hrefSeguro = (ruta) => {
  const valor = String(ruta || '').trim();
  return /^(https?:|\/)/i.test(valor) ? valor : undefined;
};
const SIN_ADJUNTOS = [];
const SIN_CC = [];

const TicketThread = ({
  mensajes,
  adjuntos = SIN_ADJUNTOS,
  onEliminarAdjunto = null,
  solicitante = '',
  cc = SIN_CC,
  onAddCc = null,
  recientesPrimero = false,
  plegarAnteriores = false,
}) => {
  // Con `recientesPrimero` el último comentario sale arriba; con `plegarAnteriores` solo ese va
  // desplegado al abrir y el resto en una línea. Cada mensaje se pliega y despliega con su
  // cabecera, y «Expandir todo / Contraer todo» actúa sobre el conjunto.
  const [manual, setManual] = useState({});
  const ordenados = recientesPrimero ? [...mensajes].reverse() : mensajes;
  const plegable = plegarAnteriores && mensajes.length > 1;
  const estaAbierto = (m, i) => {
    if (!plegable) return true;
    return m.id in manual ? manual[m.id] : i === 0;
  };
  const todosAbiertos = ordenados.every((m, i) => estaAbierto(m, i));
  const alternar = (m, i) => setManual((prev) => ({ ...prev, [m.id]: !estaAbierto(m, i) }));
  const fijarTodos = (abierto) =>
    setManual(Object.fromEntries(mensajes.map((m) => [m.id, abierto])));

  // Para/CC del correo, como en Gmail. Cada dirección que aún no recibe las
  // respuestas (ni solicitante, ni buzón de soporte, ni ya en CC) ofrece un
  // botón para añadirla al CC del ticket.
  const direcciones = (lista) =>
    lista.map((d, i) => (
      <span key={d.email} className="tickets-msg__dir">
        <span title={d.email}>{d.nombre ? `${d.nombre} <${d.email}>` : d.email}</span>
        {onAddCc && esCandidatoCc(d.email, { solicitante, cc }) && (
          <button
            type="button"
            className="tickets-msg__dir-add"
            onClick={() => onAddCc(d.email)}
            aria-label={`Añadir ${d.email} a CC`}
            title="Añadir a CC del ticket"
          >
            <UserPlus size={12} aria-hidden="true" />
          </button>
        )}
        {i < lista.length - 1 ? ',' : ''}
      </span>
    ));

  // Los es_inline=1 son imágenes del cuerpo HTML (se pintan dentro del propio
  // mensaje vía adjunto:<id>): fuera de la lista de archivos para no duplicar.
  const adjuntosDe = (comentarioId) =>
    adjuntos.filter(
      (a) => String(a.comentario_id) === String(comentarioId) && Number(a.es_inline) !== 1,
    );

  return (
    <>
      {plegable && (
        <div className="tickets-thread__barra">
          <span className="tickets-thread__cuenta">{mensajes.length} mensajes</span>
          <button
            type="button"
            className="tickets-btn tickets-btn--ghost"
            onClick={() => fijarTodos(!todosAbiertos)}
          >
            {todosAbiertos ? (
              <ChevronsUp size={14} aria-hidden="true" />
            ) : (
              <ChevronsDown size={14} aria-hidden="true" />
            )}
            {todosAbiertos ? 'Contraer todo' : 'Expandir todo'}
          </button>
        </div>
      )}
      <ol className="tickets-thread">
        {ordenados.map((m, i) => {
          if (!estaAbierto(m, i)) {
            return (
              <li key={m.id} className={`tickets-msg tickets-msg--plegado tickets-msg--${m.tipo}`}>
                <Avatar nombre={m.autor} size="sm" />
                <button
                  type="button"
                  className="tickets-msg__resumen"
                  onClick={() => alternar(m, i)}
                  aria-expanded="false"
                  aria-label={`Desplegar mensaje de ${m.autor}`}
                >
                  <ChevronRight size={14} aria-hidden="true" />
                  <span className="tickets-msg__autor">{m.autor}</span>
                  {m.tipo === 'nota' && <span className="tickets-msg__tag">Nota interna</span>}
                  <span className="tickets-msg__tiempo">{m.tiempo}</span>
                  <span className="tickets-msg__resumen-texto">
                    {String(m.texto || '')
                      .replace(/\s+/g, ' ')
                      .trim()
                      .slice(0, 160)}
                  </span>
                </button>
              </li>
            );
          }
          const adjuntosMsg = adjuntosDe(m.id);
          const para = m.para || [];
          const ccMsg = m.cc || [];
          return (
            <li key={m.id} className={`tickets-msg tickets-msg--${m.tipo}`}>
              <Avatar nombre={m.autor} size="md" />
              <div className="tickets-msg__body">
                {/* Toda la cabecera pliega el mensaje (no solo el chevron del extremo). */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  className={`tickets-msg__head${plegable ? ' tickets-msg__head--plegable' : ''}`}
                  onClick={plegable ? () => alternar(m, i) : undefined}
                >
                  <span className="tickets-msg__autor">{m.autor}</span>
                  {m.tipo === 'nota' && <span className="tickets-msg__tag">Nota interna</span>}
                  <span className="tickets-msg__tiempo">{m.tiempo}</span>
                  {plegable && (
                    <button
                      type="button"
                      className="tickets-msg__plegar"
                      onClick={(e) => {
                        e.stopPropagation();
                        alternar(m, i);
                      }}
                      aria-expanded="true"
                      aria-label={`Contraer mensaje de ${m.autor}`}
                      title="Contraer"
                    >
                      <ChevronDown size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {(para.length > 0 || ccMsg.length > 0) && (
                  <div className="tickets-msg__dest">
                    {para.length > 0 && (
                      <span className="tickets-msg__dest-grupo">
                        <span className="tickets-msg__dest-label">Para:</span> {direcciones(para)}
                      </span>
                    )}
                    {ccMsg.length > 0 && (
                      <span className="tickets-msg__dest-grupo">
                        <span className="tickets-msg__dest-label">CC:</span> {direcciones(ccMsg)}
                      </span>
                    )}
                  </div>
                )}
                <div className="tickets-msg__bubble">
                  {m.textoHtml ? <EmailHtmlBody html={m.textoHtml} adjuntos={adjuntos} /> : m.texto}
                </div>
                {adjuntosMsg.length > 0 && (
                  <ul className="tickets-msg__adjuntos">
                    {adjuntosMsg.map((a) => (
                      <li key={a.id} className="tickets-adjunto">
                        <Paperclip size={13} aria-hidden="true" />
                        <a
                          className="tickets-adjunto__nombre"
                          href={hrefSeguro(a.ruta)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {a.nombre}
                        </a>
                        {a.tamano_bytes ? (
                          <span className="tickets-adjunto__size">
                            {tamanoLegible(a.tamano_bytes)}
                          </span>
                        ) : null}
                        {onEliminarAdjunto && (
                          <button
                            type="button"
                            className="tickets-tag__remove"
                            aria-label={`Eliminar adjunto ${a.nombre}`}
                            onClick={() => onEliminarAdjunto(a.id)}
                          >
                            <X size={12} aria-hidden="true" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
};

TicketThread.propTypes = {
  mensajes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tipo: PropTypes.oneOf(['req', 'agente', 'nota']).isRequired,
      autor: PropTypes.string.isRequired,
      tiempo: PropTypes.string,
      texto: PropTypes.string,
      textoHtml: PropTypes.string,
      para: PropTypes.arrayOf(
        PropTypes.shape({ email: PropTypes.string, nombre: PropTypes.string }),
      ),
      cc: PropTypes.arrayOf(PropTypes.shape({ email: PropTypes.string, nombre: PropTypes.string })),
    }),
  ).isRequired,
  adjuntos: PropTypes.arrayOf(PropTypes.object),
  onEliminarAdjunto: PropTypes.func,
  solicitante: PropTypes.string,
  cc: PropTypes.arrayOf(PropTypes.string),
  onAddCc: PropTypes.func,
  recientesPrimero: PropTypes.bool,
  plegarAnteriores: PropTypes.bool,
};

export default TicketThread;
